/* ContinueModal.jsx — modal "¿Cómo quieres continuar?" (port de hero-v3.jsx)
 *   · WhatsApp (recomendado): arma el mensaje con los datos y abre el chat.
 *   · Cotizar aquí: dispara el cotizador en línea de 4 pasos.
 * Componente reutilizable controlado por props:
 *   { open, onClose, quote, onWhats, onQuote }
 *   - quote: estado de la cotización (marca, modelo, version, year, carroceria,
 *     motor, precio, plan, plazo, enganche) — el mismo shape de useHeroState().state.
 * También exporta los helpers compartidos: WAIcon, cap, planLabel,
 *   summaryChips, waMessage, openWhatsApp.
 */
import { useEffect, useState } from 'react';
import { fmtMXN, calcMonthly, Ico } from '../../core/heroCore';
import { waLink, waChatLink, telA10Digitos } from '../../config';

export const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
export const planLabel = (p) => p === 'puro' ? 'Plan Puro · fiscal' : 'Plan Financiero';

/* ─── WhatsApp glyph ──────────────────────────────────────────────────── */
export function WAIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M.06 24l1.68-6.13A11.86 11.86 0 0 1 .14 11.9C.14 5.34 5.48 0 12.04 0a11.82 11.82 0 0 1 8.41 3.49 11.82 11.82 0 0 1 3.49 8.41c0 6.56-5.34 11.9-11.9 11.9a11.9 11.9 0 0 1-5.68-1.45L.06 24zM6.6 20.13c1.68.99 3.28 1.59 5.43 1.59 5.45 0 9.89-4.43 9.89-9.88 0-5.45-4.44-9.89-9.89-9.89S2.15 6.39 2.15 11.84c0 2.26.66 3.95 1.76 5.72l-.99 3.63 3.68-1.06zm11.39-5.55c-.07-.12-.27-.2-.56-.34-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.62.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.42.24-.69.24-1.29.17-1.41z" />
    </svg>
  );
}

/* ─── helpers: chips & WhatsApp message ───────────────────────────────── */
export function summaryChips(s) {
  if (!s) return []; // modal genérico (header/FAB/CTA): sin chips
  const chips = [s.condicion === 'seminuevo' ? 'Seminuevo' : 'Nuevo', 'Persona física'];
  if (s.plan) chips.push(planLabel(s.plan));
  if (s.marca) chips.push(`${s.marca} ${s.modelo}`.trim());
  if (s.version) chips.push(s.version);
  if (s.year) chips.push(s.year);
  if (s.motor) chips.push(cap(s.motor));
  if (s.plazo) chips.push(`${s.plazo} meses`);
  return chips;
}
export function waMessage(s, monthlyOverride = null, leadName = null) {
  // monthlyOverride: renta real del backend (Fase 2, modo manual); sin él, mock.
  // leadName: nombre capturado en el LeadModal (opcional).
  const monthly = monthlyOverride ?? calcMonthly(s);
  const greeting = leadName
    ? `Hola Peicar 👋 Soy *${leadName}*. Quiero cotizar este auto:`
    : 'Hola Peicar 👋 Quiero cotizar este auto:';
  const L = [greeting];
  if (s.marca) L.push(`• Auto: ${s.marca} ${s.modelo} ${s.version} ${s.year}${s.condicion === 'seminuevo' ? ' (Seminuevo)' : ''}`.trim());
  if (s.carroceria || s.motor) L.push(`• Tipo: ${cap(s.carroceria)} · ${cap(s.motor)}`);
  if (s.precio) L.push(`• Precio: ${fmtMXN(s.precio)}`);
  if (s.plan) L.push(`• Plan: ${s.plan === 'puro' ? 'Puro (fiscal)' : 'Financiero'}`);
  if (s.plazo) L.push(`• Plazo: ${s.plazo} meses`);
  if (s.enganche) L.push(`• Pago inicial: ${fmtMXN(s.enganche)}`);
  if (monthly) L.push(`• Renta estimada: ${fmtMXN(monthly)}/mes`);
  return L.join('\n');
}
export function openWhatsApp(s, monthlyOverride = null, leadName = null) {
  window.open(waLink(waMessage(s, monthlyOverride, leadName)), '_blank', 'noopener');
}

/* Los 5 datos que van en la plantilla `cotizacion_web_seguimiento` (el 6º, el
 * folio, lo pone el backend desde el Budget).
 *
 * El auto va CORTO a propósito: s.version trae el trim completo de RODAC
 * ("MARCH SENSE L4 ISM AUT 4 ABS CA SE TELA CD SQ SB"), que en un chat es
 * ilegible. El asesor tiene la cotización completa del otro lado. */
export function plantillaParams(s, monthlyOverride = null) {
  const monthly = monthlyOverride ?? calcMonthly(s);
  return {
    // Sin año y sin condición: el mensaje los lista en renglones aparte, y meter
    // "(seminuevo)" aquí costaba 13 caracteres que sacaban el texto del límite.
    auto: `${s.marca || ''} ${s.modelo || ''}`.replace(/\s+/g, ' ').trim(),
    anio: s.year ? String(s.year) : '',
    plazo: s.plazo ? `${s.plazo} meses` : '',
    pagoInicial: s.enganche ? fmtMXN(s.enganche) : '',
    renta: monthly ? fmtMXN(monthly) : '',
  };
}

/* ═══════════════════════════════════════════════════════════════════════
   Modal · ¿Cómo quieres continuar?
   ═══════════════════════════════════════════════════════════════════════ */
/* waState — lo maneja HeroV3:
 *   idle      → nada mandado; el modal ofrece los dos caminos de siempre.
 *   sending   → en vuelo.
 *   sent      → Meta lo aceptó pero el acuse no llegó a tiempo. NO afirmamos que
 *               llegó: mensaje tibio y a esperar.
 *   delivered → confirmado en su teléfono. Aquí sí se le invita a chatear con
 *               Aria, porque el hilo ya existe de su lado.
 *   failed    → nunca le llegó (131049 y compañía). Se le dan las tres salidas:
 *               otro número, abrir el chat a mano, o cotizar aquí. */
export default function ContinueModal({
  open, onClose, quote, onWhats, onQuote,
  waState = 'idle', waTelefono = null, onAbrirWa = null, onOtroNumero = null,
}) {
  const [otroTel, setOtroTel] = useState('');
  const [reintentando, setReintentando] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const chips = summaryChips(quote);
  const entregado = waState === 'delivered';
  const enviado = waState === 'sent';
  const enviando = waState === 'sending';
  const falló = waState === 'failed';
  const otroTelOk = telA10Digitos(otroTel) !== null;
  // "55 8353 9764" — se le muestra para que sepa a qué número buscar.
  const telVisible = (waTelefono || '').replace(/^52/, '').replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="modal-x" onClick={onClose} aria-label="Cerrar">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <div className="modal-head">
          <div className="modal-ic" style={entregado ? { background: 'rgba(0,186,110,.12)', color: 'var(--pei-green-true)' } : undefined}>
            {entregado || enviado ? <Ico.Check /> : <Ico.Sparkle />}
          </div>
          <h3>
            {entregado ? 'Aria ya te escribió'
              : falló ? 'No pudimos escribirte'
                : enviado ? '¡Listo, te escribimos!'
                  : '¿Cómo quieres continuar?'}
          </h3>
          <p>
            {entregado
              ? `Tu cotización ya está en tu WhatsApp${telVisible ? ` (${telVisible})` : ''}. Contéstale a Aria por ahí: ella te atiende y te ajusta lo que quieras al momento.`
              : falló
                ? 'El mensaje no llegó a ese número. Prueba con otro, ábrelo tú mismo, o termina tu cotización aquí.'
                : enviado
                  ? `Mandamos tu cotización al ${telVisible || 'WhatsApp que nos diste'}. Si en un minuto no la ves, revisa que el número esté bien.`
                  : 'Tenemos tu información lista. Elige el camino que prefieras.'}
          </p>
        </div>

        {chips.length > 0 && !entregado && !enviado && !falló && (
          <div className="modal-chips">
            {chips.map((c, i) => <span key={i} className="mchip">{c}</span>)}
          </div>
        )}

        {/* Reintento con otro número: el motivo #1 de que no llegue es un
            teléfono mal escrito o sin WhatsApp, y hasta ahora la única salida
            era abandonar. Va ARRIBA de los caminos porque es la que más
            probablemente resuelve. */}
        {falló && onOtroNumero && (
          <form
            className="cz-form"
            style={{ marginTop: 4, marginBottom: 14 }}
            onSubmit={async (e) => {
              e.preventDefault();
              if (!otroTelOk || reintentando) return;
              setReintentando(true);
              await onOtroNumero(telA10Digitos(otroTel));
              setReintentando(false);
              setOtroTel('');
            }}
          >
            <label>
              Prueba con otro WhatsApp
              <input
                value={otroTel}
                onChange={(ev) => setOtroTel(ev.target.value)}
                placeholder="55 1234 5678"
                autoComplete="tel"
                inputMode="tel"
                className={otroTel && !otroTelOk ? 'cz-invalid' : ''}
              />
              {otroTel && !otroTelOk && (
                <span className="cz-hint">Necesito los 10 dígitos de un celular con WhatsApp.</span>
              )}
            </label>
            <button type="submit" className="cgo wide" disabled={!otroTelOk || reintentando}>
              {reintentando ? 'Enviando…' : 'Mandarlo a este número'} {!reintentando && <Ico.Arrow />}
            </button>
          </form>
        )}

        <div className="paths">
          {falló ? (
            /* wa.me CON el texto prellenado: el mensaje que no pudimos mandarle
               nosotros lo manda él con un toque. Es el camino de siempre y el
               único que WhatsApp no puede bloquear. */
            <button className="path wa" onClick={onAbrirWa}>
              <span className="path-ic wa-ic"><WAIcon width="22" height="22" /></span>
              <span className="path-tx">
                <b>Abrirlo yo mismo</b>
                <span>Tu mensaje ya va escrito. Solo dale enviar.</span>
              </span>
              <span className="path-go"><Ico.Arrow /></span>
            </button>
          ) : entregado || enviado ? (
            /* La conversación ya existe: el link va SIN texto prellenado, solo
               abre el chat donde está el mensaje que le acabamos de mandar. */
            <a className="path wa" href={waChatLink()} target="_blank" rel="noopener noreferrer">
              <span className="path-ic wa-ic"><WAIcon width="22" height="22" /></span>
              <span className="path-tx">
                <b>
                  {entregado ? 'Chatear con Aria' : 'Abrir WhatsApp'}
                  {entregado && <span className="path-badge">Recomendado</span>}
                </b>
                <span>
                  {entregado
                    ? 'Te responde al instante y ajusta tu cotización.'
                    : 'Ahí está tu cotización esperándote.'}
                </span>
              </span>
              <span className="path-go"><Ico.Arrow /></span>
            </a>
          ) : (
            <button className="path wa" onClick={onWhats} disabled={enviando}>
              <span className="path-ic wa-ic"><WAIcon width="22" height="22" /></span>
              <span className="path-tx">
                <b>
                  {enviando ? 'Enviando…' : 'Continuar por WhatsApp'}
                  {!enviando && <span className="path-badge">Recomendado</span>}
                </b>
                <span>
                  {enviando
                    ? 'Te estamos mandando el mensaje.'
                    : 'Te mandamos tu cotización al WhatsApp, sin que escribas nada.'}
                </span>
              </span>
              <span className="path-go"><Ico.Arrow /></span>
            </button>
          )}

          {/* "Cotizar aquí" se ofrece SIEMPRE, también con el WhatsApp ya
              entregado. Antes se ocultaba en cuanto Aria escribía, con la idea
              de que el chat ya se hacía cargo; en la práctica dejaba sin salida
              a quien prefiere terminar en la página, y el modal se volvía un
              callejón de un solo camino. Los dos conviven: cuál preferimos lo
              dice el badge "Recomendado" del de arriba, no esconder el otro. */}
          <button className="path here" onClick={onQuote} disabled={enviando || reintentando}>
            <span className="path-ic here-ic"><Ico.Calc /></span>
            <span className="path-tx">
              <b>Cotizar aquí</b>
              <span>Termina en línea en 4 pasos, sin salir de la página.</span>
            </span>
            <span className="path-go"><Ico.Arrow /></span>
          </button>
        </div>

        <div className="modal-foot">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
          Tus datos están protegidos. Consulta nuestro <a href="/aviso-de-privacidad">aviso de privacidad</a>.
        </div>
      </div>
    </div>
  );
}
